/*
 * Floating "Call now" button, bottom left, on every page.
 *
 * A real <a href="tel:..."> rather than a click handler, so it behaves the way
 * a phone number should: long-press to copy, open in a new tab, right-click,
 * and it still works if this script's own listeners never attach.
 *
 * TWO THINGS IT HAS TO AVOID
 *
 * 1. The mobile sticky Add to Cart bar. That is fixed to the bottom of every
 *    product page and spans the full width, so a button at bottom:16px sits on
 *    top of it. The bar announces itself with a `show-sticky` class, so the
 *    button watches for that and lifts clear while it is up.
 *
 * 2. The sticky bar's expanded panel. When that opens the body gets
 *    `show-mobile-options` and the panel covers the lower half of the screen;
 *    the button hides rather than floating over it.
 *
 * The chat bubble sits bottom RIGHT, which is why this goes bottom left.
 *
 * z-index is deliberately modest: above the page, below drawers and modals, so
 * it can never sit over the cart or a dialog.
 */

(function () {
  'use strict';

  var PHONE = '+14374221187';
  var LABEL = 'Call now';

  var GAP = 12; // breathing room between the button and the bar
  var STYLE_ID = 'civia-call-style';
  var MARK = 'data-civia-call';

  var CSS =
    '.civia-call{position:fixed;left:16px;bottom:16px;z-index:60;' +
    'display:inline-flex;align-items:center;gap:9px;' +
    'padding:12px 20px;border-radius:999px;' +
    'background:#232323;color:#fff;text-decoration:none;' +
    'font-size:15px;font-weight:600;line-height:1;white-space:nowrap;' +
    'box-shadow:0 4px 14px rgba(0,0,0,.22);' +
    '-webkit-tap-highlight-color:transparent;touch-action:manipulation;' +
    'transition:transform .25s ease,opacity .25s ease,bottom .25s ease}' +
    '.civia-call__icon{width:17px;height:17px;fill:currentColor;flex:none}' +
    /* Hover only where a pointer can hover - the round-28 lesson. On touch,
       :hover sticks after a tap and the button would stay lifted. */
    '@media (hover:hover) and (pointer:fine){' +
    '.civia-call:hover{transform:translateY(-2px);color:#fff}}' +
    '.civia-call:focus-visible{outline:2px solid #232323;outline-offset:3px}' +
    /* No fixed lift value here: the bar is about 64px on a phone but far
       taller on desktop, where it also shows the image, title, price and
       options. sync() measures it and sets the offset inline. */
    '.civia-call.is-hidden{opacity:0;pointer-events:none;transform:translateY(8px)}' +
    '@media (max-width:640px){.civia-call{left:12px;bottom:12px;' +
    'padding:11px 17px;font-size:14px}}' +
    '@media (prefers-reduced-motion:reduce){.civia-call{transition:none}}';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* How far to lift the button so it clears the sticky bar.
   *
   * Two things the obvious `bar.getBoundingClientRect().height` misses:
   *
   *   1. The bar is NOT flush to the bottom - it computes to `bottom: 68px` on
   *      desktop. Measuring only its height left the button 68px short, still
   *      sitting inside the bar. The offset has to be part of the sum.
   *
   *   2. The bar slides in with a transform. A rect read while that is running
   *      catches it mid-slide. offsetHeight is layout and ignores transforms,
   *      and the computed `bottom` is a static value, so both are stable from
   *      the first frame.
   */
  function liftFor(bar) {
    var offset = parseFloat(window.getComputedStyle(bar).bottom);
    if (isNaN(offset) || offset < 0) offset = 0;

    var lift = Math.round(bar.offsetHeight + offset) + GAP;

    /* Guard rail. The offset is whatever the bar reports, and a bar parked at
       an unexpected value would push the button clean off the top of the
       screen - an invisible button is far worse than one overlapping a bar.
       Anything past a sensible fraction of the viewport is not a real bar
       position, so fall back to resting. */
    return lift > Math.round(window.innerHeight * 0.45) ? 0 : lift;
  }

  /* The chat bubble, so the two float as a matched pair.
   *
   * Its own id first, then a shape-and-position search, so swapping chat apps
   * degrades to "no alignment" rather than to a button pinned to a stale
   * selector: a small square fixed box tucked into the bottom-right corner is
   * what every chat launcher is.
   */
  function findChat() {
    var known = document.getElementById('echo-widget-button');
    if (known && known.offsetHeight) return known;

    var all = document.body.getElementsByTagName('*');

    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.hasAttribute(MARK)) continue;

      /* Cheap layout reads first; getComputedStyle only for real candidates. */
      var h = el.offsetHeight;
      if (h < 36 || h > 140 || Math.abs(el.offsetWidth - h) > 12) continue;

      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none') continue;

      var below = parseFloat(cs.bottom);
      var side = parseFloat(cs.right);
      if (isNaN(below) || isNaN(side) || below > 200 || side > 200) continue;

      return el;
    }

    return null;
  }

  /* Match the two centres, not the two bottom edges. The bubble is a 48px
   * circle and the button a 41px pill, so equal bottoms leave them visibly
   * off-axis - which is exactly what the button being "a bit low" was.
   *
   * Layout values rather than a rect, for the same reason as liftFor: the
   * bubble animates in, and in a hidden tab that animation never advances, so
   * a rect read there measures a position the bubble never rests at.
   */
  function alignedTo(chat, link) {
    var below = parseFloat(window.getComputedStyle(chat).bottom);
    if (isNaN(below) || below < 0) return 0;

    var centre = below + chat.offsetHeight / 2;
    var bottom = Math.round(centre - link.offsetHeight / 2);

    /* Anything wild is not a chat launcher; leave the button where it rests. */
    return bottom > 0 && bottom < Math.round(window.innerHeight * 0.25) ? bottom : 0;
  }

  function build() {
    var link = document.createElement('a');
    link.className = 'civia-call';
    link.setAttribute(MARK, '');
    link.href = 'tel:' + PHONE;
    link.setAttribute('aria-label', LABEL + ' on ' + PHONE);

    link.innerHTML =
      '<svg class="civia-call__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.58 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.6a1 1 0 0 1-.25 1z"/></svg>' +
      '<span>' + LABEL + '</span>';

    document.body.appendChild(link);
    return link;
  }

  function start() {
    if (!document.body || document.querySelector('[' + MARK + ']')) return;

    injectStyle();
    var link = build();

    var chat = null;

    function restingBottom() {
      if (!chat || !chat.isConnected) chat = findChat();
      return chat ? alignedTo(chat, link) : 0;
    }

    function sync() {
      /* The panel covers the lower screen - do not float over it. */
      var panelOpen = document.body.classList.contains('show-mobile-options');
      link.classList.toggle('is-hidden', panelOpen);

      var bar = document.querySelector('sticky-add-to-cart');
      var barUp = !!bar && bar.classList.contains('show-sticky');
      var lift = barUp && !panelOpen;

      link.classList.toggle('is-lifted', lift);

      /* Measured, not assumed. A hardcoded 84px cleared the phone bar but left
         the button sitting inside the desktop one. liftFor returns 0 when it
         cannot trust the number, which leaves the button at its resting spot. */
      var offset = lift ? liftFor(bar) : restingBottom();
      link.style.bottom = offset ? offset + 'px' : '';
    }

    sync();

    if (!window.MutationObserver) return;

    /* The bar toggles its own class on scroll; the body class changes when the
       panel opens. Watching the attribute is cheaper than polling either. */
    var observer = new window.MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    var bar = document.querySelector('sticky-add-to-cart');
    if (bar) observer.observe(bar, { attributes: true, attributeFilter: ['class'] });

    /* The chat widget is injected by a third-party script long after load, so
       the first sync almost always runs before it exists. Retry until it turns
       up, then stop - no point scanning the tree forever. */
    var tries = 0;
    var hunt = window.setInterval(function () {
      tries++;
      sync();
      if (chat || tries >= 16) window.clearInterval(hunt);
    }, 500);

    /* The bar's height and offset both differ by breakpoint. */
    var pending = false;
    window.addEventListener('resize', function () {
      if (pending) return;
      pending = true;

      window.setTimeout(function () {
        pending = false;
        sync();
      }, 150);
    }, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
